import React from 'react';
import { Target, Zap, Rocket, Users, PenTool, Star } from 'lucide-react';
import styles from './Services.module.css';
import { useScrollAnimation, useStaggerAnimation } from '../hooks/useScrollAnimation';

const MAIN_FEATURES_LEFT = [
  'Post & comment upvotes',
  'Custom written comments, your tone',
  'Drip-feed delivery — no sudden spikes',
  'Target specific subreddits',
  'Full dashboard tracking',
  'Monthly PDF report',
];

const MAIN_FEATURES_RIGHT = [
  'Comment + post karma mix',
  'Subreddit-natural tone & activity',
  'Gradual build — no flags',
  'Unlock restricted communities',
  'Aged activity patterns',
];

const MINI_SERVICES = [
  {
    icon: Rocket,
    title: 'One-time Boosts',
    desc: 'Need a quick push for a specific post or product launch? Buy a one-off boost without committing to a subscription.',
  },
  {
    icon: Users,
    title: 'Subreddit Growth',
    desc: 'We grow your own subreddit with real subscribers who engage with new posts — turning it into a community asset for your brand.',
  },
  {
    icon: PenTool,
    title: 'Content + Posting',
    desc: "We don't just boost — we write and post content for you using our accounts, in the voice your target subreddits expect.",
  },
];

function FeatureList({ items }) {
  return (
    <ul className={styles.featureList}>
      {items.map((item) => (
        <li key={item} className={styles.featureItem}>
          <span className={styles.featureCheck} />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function Services() {
  const headRef = useScrollAnimation();
  const cardsRef = useStaggerAnimation();
  const miniRef = useStaggerAnimation();

  const handleScroll = (href) => {
    const target = document.querySelector(href);
    if (target) window.scrollTo({ top: target.offsetTop - 80, behavior: 'smooth' });
  };

  return (
    <section className={styles.section} id="services">
      <div className="container">
        <div className={`${styles.head} fade-up`} ref={headRef}>
          <div>
            <div className="section-label">Services</div>
            <h2 className="section-title">
              Everything your brand
              <br />
              needs on Reddit.
            </h2>
          </div>
          <p className="section-sub" style={{ maxWidth: 340 }}>
            Two core services. One clear goal — making Reddit work for your business
            without you having to learn how Reddit works.
          </p>
        </div>

        {/* Main two cards */}
        <div className={styles.mainGrid} ref={cardsRef}>
          {/* Engagement */}
          <div className={`${styles.card} ${styles.featured} stagger-child`}>
            <div className={styles.cardBadge}>
              <Star size={12} strokeWidth={3} fill="currentColor" />
              Most popular
            </div>
            <div className={styles.cardIcon}>
              <Target size={36} strokeWidth={1.5} className="text-accent" />
            </div>
            <h3 className={styles.cardTitle}>Reddit Engagement</h3>
            <p className={styles.cardDesc}>
              Monthly subscription. We upvote your posts, place contextual comments, and push
              your content to the top of target subreddits — all from aged, credible accounts.
            </p>
            <FeatureList items={MAIN_FEATURES_LEFT} />
            <div className={styles.cardFooter}>
              <div className={styles.cardPrice}>
                From <strong>$49</strong>
                <span className={styles.pricePer}>/mo</span>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => handleScroll('#pricing')}
              >
                See plans
              </button>
            </div>
          </div>

          {/* Karma */}
          <div className={`${styles.card} stagger-child`}>
            <div className={styles.cardIcon}>
              <Zap size={36} strokeWidth={1.5} className="text-accent" />
            </div>
            <h3 className={styles.cardTitle}>Karma Building</h3>
            <p className={styles.cardDesc}>
              One-time service. We grow the karma on your Reddit account so you can post in
              restricted subreddits, gain moderator trust, and avoid spam filters.
            </p>
            <FeatureList items={MAIN_FEATURES_RIGHT} />
            <div className={styles.cardFooter}>
              <div className={styles.cardPrice}>
                From <strong>$29</strong>
                <span className={styles.pricePer}> one-time</span>
              </div>
              <button
                className="btn btn-outline"
                onClick={() => handleScroll('#pricing')}
              >
                See packages
              </button>
            </div>
          </div>
        </div>

        {/* Mini services */}
        <div className={styles.miniGrid} ref={miniRef}>
          {MINI_SERVICES.map((s) => (
            <div key={s.title} className={`${styles.mini} stagger-child`}>
              <div className={styles.miniIcon}>
                <s.icon size={24} strokeWidth={2} className="text-accent" />
              </div>
              <h4 className={styles.miniTitle}>{s.title}</h4>
              <p className={styles.miniDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
